import React, { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Camera, Search, Upload, X } from 'lucide-react';

import api, { getAssetUrl } from '../../services/api';
import { formatPersonName } from '../../utils/formatters';
import FacialAccessActions from '../../components/FacialAccessActions';

const INITIAL_FORM = {
  nome_completo: '',
  documento: '',
  contato: '',
  placa_carro: '',
  morador_id: '',
  foto_url: null,
};

const VisitanteModal = ({ isOpen, onClose, moradores, visitanteParaEditar }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [imagemExpandida, setImagemExpandida] = useState(null);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const isSecureContextForCamera =
    typeof window !== 'undefined' &&
    (window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    if (isOpen) {
      if (visitanteParaEditar) {
        setFormData({
          nome_completo: visitanteParaEditar.nome_completo || '',
          documento: visitanteParaEditar.documento || '',
          contato: visitanteParaEditar.contato || '',
          placa_carro: visitanteParaEditar.placa_carro || '',
          morador_id: visitanteParaEditar.morador_id || '',
          foto_url: visitanteParaEditar.foto_url || null,
        });
        setSearchTerm(visitanteParaEditar.nome_morador || '');
        setPreviewUrl(getAssetUrl(visitanteParaEditar.foto_url));
      } else {
        setFormData(INITIAL_FORM);
        setSearchTerm('');
        setPreviewUrl(null);
      }
      setShowResults(false);
      setSelectedFile(null);
      setCameraError('');
      setCameraOpen(false);
      setVideoDevices([]);
      setSelectedDeviceId('');
    setImagemExpandida(null);
    }
  }, [isOpen, visitanteParaEditar]);

  useEffect(() => {
    if (!selectedFile) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const loadVideoDevices = React.useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setVideoDevices([]);
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      setVideoDevices(cameras);
      setSelectedDeviceId((currentId) => {
        if (currentId && cameras.some((device) => device.deviceId === currentId)) {
          return currentId;
        }
        return cameras[0]?.deviceId || '';
      });
    } catch {
      setVideoDevices([]);
    }
  }, []);

  const handleCameraError = React.useCallback((error) => {
    const errorName = error?.name || '';

    if (!isSecureContextForCamera) {
      setCameraError('A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.');
      return;
    }

    if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
      setCameraError('O navegador bloqueou a camera. Libere a permissao da camera no cadeado do site.');
      return;
    }

    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
      setCameraError('Nenhuma camera foi encontrada neste computador.');
      return;
    }

    if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
      setCameraError('A camera esta ocupada por outro programa, como Zoom, Meet ou Teams.');
      return;
    }

    setCameraError('Nao foi possivel abrir a camera neste navegador. Envie uma imagem do computador.');
  }, [isSecureContextForCamera]);

  const handleOpenCamera = React.useCallback(async () => {
    setSelectedFile(null);
    setCameraError('');

    if (!isSecureContextForCamera) {
      setCameraOpen(false);
      setCameraError('A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.');
      return;
    }

    await loadVideoDevices();
    setCameraOpen(true);
  }, [isSecureContextForCamera, loadVideoDevices]);

  const handleUserMedia = React.useCallback(() => {
    setCameraError('');
    loadVideoDevices();
  }, [loadVideoDevices]);

  const handleCloseCamera = React.useCallback(() => {
    setCameraOpen(false);
    setCameraError('');
  }, []);

  const capture = React.useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setCameraError('Nao foi possivel capturar a imagem da camera.');
      return;
    }

    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], 'visitante.jpg', { type: 'image/jpeg' });
        setSelectedFile(file);
        setCameraOpen(false);
        setCameraError('');
      })
      .catch(() => {
        setCameraError('Nao foi possivel capturar a imagem da camera.');
      });
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setCameraOpen(false);
    setSelectedFile(file);
    setCameraError('');
  };

  const abrirImagem = (src, alt) => {
    if (src) {
      setImagemExpandida({ src, alt });
    }
  };

  const aplicarMascaraTelefone = (valor) => {
    if (!valor) return '';
    let telefone = valor.replace(/\D/g, '');
    telefone = telefone.replace(/(\d{2})(\d)/, '($1) $2');
    telefone = telefone.replace(/(\d{5})(\d)/, '$1-$2');
    return telefone.substring(0, 15);
  };

  const limparMascara = (valor) => valor.replace(/\D/g, '');

  const moradoresFiltrados = (moradores || []).filter((m) =>
    String(m.nome ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(m.unidade ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectMorador = (morador) => {
    setFormData({ ...formData, morador_id: morador.id });
    setSearchTerm(`${morador.nome || 'Morador sem nome'} - ${morador.unidade ? `Unidade: ${morador.unidade}` : 'Sem unidade informada'}`);
    setShowResults(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.morador_id) return alert('Selecione um morador responsável!');

    const dadosParaEnviar = new FormData();
    dadosParaEnviar.append('nome_completo', formatPersonName(formData.nome_completo));
    dadosParaEnviar.append('documento', formData.documento.replace(/\D/g, ''));
    dadosParaEnviar.append('contato', limparMascara(formData.contato || ''));
    dadosParaEnviar.append('placa_carro', formData.placa_carro || '');
    dadosParaEnviar.append('morador_id', String(formData.morador_id));
    if (selectedFile) {
      dadosParaEnviar.append('foto', selectedFile, selectedFile.name);
    }

    try {
      if (visitanteParaEditar) {
        await api.put(`/visitantes/${visitanteParaEditar.id}`, dadosParaEnviar);
        alert('Visitante atualizado com sucesso!');
      } else {
        await api.post('/visitantes/', dadosParaEnviar);
        alert('Entrada de visitante registrada!');
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar visitante:', error);
      alert('Erro ao salvar visitante');
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b bg-gray-50/50 px-4 py-4 md:px-5">
          <h2 className="text-xl font-bold text-slate-800">
            {visitanteParaEditar ? 'Editar Visitante' : 'Registrar Entrada'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 md:px-5 md:py-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Nome do Visitante</label>
                <input
                  type="text"
                  required
                  className="w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-slate-900 outline-none"
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                  onBlur={() => setFormData((current) => ({ ...current, nome_completo: formatPersonName(current.nome_completo) }))}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Documento (CPF)</label>
                  <input
                    type="text"
                    placeholder="Apenas números"
                    className="w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-slate-900 outline-none"
                    value={formData.documento}
                    maxLength={11}
                    onChange={(e) => setFormData({ ...formData, documento: e.target.value.replace(/\D/g, '') })}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Telefone</label>
                  <input
                    type="text"
                    placeholder="(00) 00000-0000"
                    className="w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-slate-900 outline-none"
                    value={aplicarMascaraTelefone(formData.contato)}
                    onChange={(e) => setFormData({ ...formData, contato: limparMascara(e.target.value) })}
                  />
                </div>
              </div>

              <label className="block text-sm font-semibold text-gray-700">Placa do veículo
                <input aria-label="Placa do veículo" value={formData.placa_carro} maxLength={8} placeholder="ABC1234 ou ABC1D23" onChange={(e) => setFormData({ ...formData, placa_carro: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') })} className="mt-1 w-full rounded-xl border border-gray-300 p-3 font-normal" />
              </label>
              <div className="relative">
                <label className="mb-1 block text-sm font-semibold text-gray-700">Morador Responsável</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Digite o nome ou unidade..."
                    className="w-full rounded-xl border border-gray-300 p-3 pl-10 focus:ring-2 focus:ring-slate-900 outline-none"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowResults(true);
                    }}
                    onFocus={() => setShowResults(true)}
                  />
                </div>

                {showResults && searchTerm.length > 0 && (
                  <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
                    {moradoresFiltrados.length > 0 ? (
                      moradoresFiltrados.map((m) => (
                        <div
                          key={m.id}
                          onClick={() => handleSelectMorador(m)}
                          className="flex cursor-pointer items-center justify-between border-b p-3 hover:bg-gray-50 last:border-0"
                        >
                          <span className="text-sm font-medium text-slate-700">{m.nome || 'Morador sem nome'}</span>
                          <span className="rounded-md bg-blue-50 px-2 py-1 text-xs font-bold text-blue-600">{m.unidade ? `Unidade: ${m.unidade}` : 'Sem unidade informada'}</span>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-center text-sm italic text-gray-500">Nenhum morador encontrado</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Foto do Visitante</label>

              <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="relative mx-auto flex min-h-[180px] max-w-sm items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white sm:min-h-[220px]">
                {cameraOpen && !cameraError && isSecureContextForCamera ? (
                  <>
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      mirrored
                      screenshotFormat="image/jpeg"
                      className="h-[180px] w-full object-contain sm:h-[220px]"
                      videoConstraints={selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : { facingMode: 'user' }}
                      onUserMedia={handleUserMedia}
                      onUserMediaError={handleCameraError}
                    />
                    <div className="absolute bottom-3 right-3 flex gap-2">
                      <button
                        type="button"
                        onClick={handleCloseCamera}
                        className="rounded-full border border-slate-200 bg-white p-2 text-slate-950 shadow-sm transition hover:bg-slate-100"
                        style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                        aria-label="Fechar camera"
                      >
                        <X size={20} className="text-slate-950" />
                      </button>
                      <button
                        type="button"
                        onClick={capture}
                        className="rounded-full bg-slate-900 p-2 text-white shadow-lg"
                      >
                        <Camera size={20} />
                      </button>
                    </div>
                  </>
                ) : previewUrl ? (
                  <button
                    type="button"
                    onClick={() => abrirImagem(previewUrl, 'Pré-visualização do visitante')}
                    className="h-full w-full"
                    title="Ampliar foto"
                  >
                    <img src={previewUrl} alt="Pré-visualização do visitante" className="h-[180px] w-full object-contain sm:h-[220px]" />
                  </button>
                ) : (
                  <div className="flex h-[180px] w-full items-center justify-center p-6 text-center text-sm text-slate-500 sm:h-[220px]">
                    {cameraError || 'Escolha se deseja abrir a camera ou enviar uma foto do computador.'}
                  </div>
                )}
                </div>

                {cameraOpen && videoDevices.length > 1 && (
                  <select
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                    className="mt-3 w-full rounded-xl border border-gray-300 p-3 focus:ring-2 focus:ring-slate-900 outline-none"
                  >
                    {videoDevices.map((device, index) => (
                      <option key={device.deviceId || index} value={device.deviceId}>
                        {device.label || `Camera ${index + 1}`}
                      </option>
                    ))}
                  </select>
                )}

                <div className="mx-auto mt-3 grid max-w-sm grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleOpenCamera}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <Camera size={18} />
                    Abrir camera
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    <Upload size={18} />
                    Enviar foto
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  onChange={handleFileChange}
                />
                {previewUrl && (
                  <button
                    type="button"
                    onClick={() => abrirImagem(previewUrl, 'Pré-visualização do visitante')}
                    className="mt-3 flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5 text-left transition hover:bg-slate-50"
                  >
                    <img src={previewUrl} alt="Pré-visualização do visitante" className="h-16 w-16 rounded-lg border border-gray-200 bg-white object-contain" />
                    <span className="text-sm text-slate-600">Pré-visualização da foto selecionada</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <button type="submit" className="mt-5 w-full rounded-xl bg-slate-900 py-3 text-sm font-bold text-white transition hover:bg-slate-800 shadow-lg">
            {visitanteParaEditar ? 'Salvar Alterações' : 'Confirmar Entrada'}
          </button>

          <FacialAccessActions
            pessoa={visitanteParaEditar ? {
              tipo: 'visitante',
              id: visitanteParaEditar.id,
              nome: visitanteParaEditar.nome_completo,
              foto_url: visitanteParaEditar.foto_url,
              descricao: visitanteParaEditar.nome_morador || 'Visitante',
            } : null}
          />
        </form>
      </div>
    </div>
      {imagemExpandida && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setImagemExpandida(null)}
        >
          <button
            type="button"
            onClick={() => setImagemExpandida(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
          >
            <X size={22} />
          </button>
          <img
            src={imagemExpandida.src}
            alt={imagemExpandida.alt}
            className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default VisitanteModal;
